'use client';

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';
import Link from 'next/link';
import { useMediaQuery } from '@/components/hooks/use-media-query';
import { Button } from '@/components/ui/button';
import { Image } from '@/components/ui/Image';
import { NavBar } from '@/components/ui/NavBar';
import { URLS } from '@/lib/consts';
import { cls } from '@/lib/utils';
import { childElementMotionProperties } from './animations';

const LOGO_URI = 'cornerstone-homes-logo_amrijn';

function Header() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isVisible, setIsVisible] = useState(true);
  const timeoutId = useRef<number | null>(null);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    function handleScroll() {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY;
      const scrollDelta = currentScrollY - lastScrollY;

      if (isScrollingDown && scrollDelta >= 10) {
        setIsVisible(false);

        if (timeoutId.current) window.clearTimeout(timeoutId.current);
      } else {
        if (timeoutId.current) window.clearTimeout(timeoutId.current);
      }

      timeoutId.current = window.setTimeout(() => {
        setIsVisible(true);
      }, 750);

      lastScrollY = currentScrollY;
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);

      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <motion.header
      id="header"
      className={cls`
        fixed top-0 left-0 w-full
        grid grid-cols-body
        bg-primary text-primary-foreground
        min-h-header-mobile md:min-h-header
        z-50
        shadow-md
      `}
      style={{ opacity: isVisible ? 1 : 0 }}
      initial={{ opacity: 1 }}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: 0.3 }}
    >
      <nav
        className={cls`
          col-start-2
          flex
          flex-1
          flex-row
          justify-between
          items-center
          self-stretch
        `}
      >
        <div
          className={cls`
            flex
            flex-row
            justify-center
            items-center
            self-stretch
            gap-4
          `}
        >
          <Button
            className={cls`
              min-h-header-mobile
              md:min-h-header
              aspect-square
              border-0
              relative
            `}
            variant="ghost"
            asChild
          >
            <motion.div {...childElementMotionProperties}>
              <Link
                className={cls`w-full h-full aspect-square`}
                href={URLS.HOME}
              >
                <Image
                  fill
                  className={cls`flex justify-center items-center text-center text-lg font-prata`}
                  src={LOGO_URI}
                  alt="Cornerstone Homes Logo"
                  sizes="10vw"
                />
              </Link>
            </motion.div>
          </Button>

          <motion.div
            className={cls`hidden md:block`}
            {...childElementMotionProperties}
          >
            <span className={cls`block text-4xl text-primary-foreground w-min font-prata`}>
              Cornerstone Homes
            </span>
          </motion.div>
        </div>

        <div className="flex items-center gap-4">
          <a
            href="tel:918-344-1731"
            className="hidden md:flex items-center gap-2 text-primary-foreground hover:text-primary-foreground/80 transition-colors"
          >
            <Phone size={18} />
            <span className="text-sm font-medium">(918) 344-1731</span>
          </a>

          <NavBar isDesktop={isDesktop} />
        </div>
      </nav>
    </motion.header>
  );
}

export default Header;
